import React, { useState, useEffect } from "react";
import { Modal, Form, Input, Space } from "antd";
import { LockOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";

import { useNotification } from "../../shared/hooks";
import { changePassword } from "./api";

interface ChangePasswordModalProps {
  open: boolean;
  onClose: () => void;
  onLogout: () => void;
}

export const ChangePasswordModal: React.FC<ChangePasswordModalProps> = ({
  open,
  onClose,
  onLogout,
}) => {
  const { t } = useTranslation();
  const { showSuccess, notifyError } = useNotification();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) {
      form.resetFields();
    }
  }, [open, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      changePassword(
        {
          current_password: values.current_password,
          new_password: values.new_password,
        },
        () => {
          setLoading(false);
          showSuccess(t("password_changed_please_login"));
          onLogout();
        },
        (err) => {
          setLoading(false);
          notifyError(t("failed_to_change_password"), err);
        }
      );
    } catch {
      // Form validation failed
    }
  };

  return (
    <Modal
      title={
        <Space>
          <LockOutlined />
          {t("change_password")}
        </Space>
      }
      open={open}
      onCancel={onClose}
      onOk={handleSubmit}
      okText={t("save")}
      cancelText={t("cancel")}
      confirmLoading={loading}
      destroyOnHidden
    >
      <Form form={form} layout="vertical">
        <Form.Item
          name="current_password"
          label={t("current_password")}
          rules={[{ required: true, message: t("please_enter_current_password") }]}
        >
          <Input.Password placeholder={t("enter_current_password")} />
        </Form.Item>

        <Form.Item
          name="new_password"
          label={t("new_password")}
          rules={[
            { required: true, message: t("please_enter_new_password") },
            { min: 6, message: t("password_min_length") },
          ]}
        >
          <Input.Password placeholder={t("enter_new_password")} />
        </Form.Item>

        <Form.Item
          name="confirm_password"
          label={t("confirm_password")}
          dependencies={["new_password"]}
          rules={[
            { required: true, message: t("please_confirm_password") },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue("new_password") === value) {
                  return Promise.resolve();
                }
                return Promise.reject(new Error(t("passwords_do_not_match")));
              },
            }),
          ]}
        >
          <Input.Password placeholder={t("confirm_new_password")} />
        </Form.Item>
      </Form>
    </Modal>
  );
};
